import { useCallback, useEffect, useState } from 'react'
import { digitsOnly, isValidCpf } from '@/utils/cpf'
import { resolveCaptcha } from '@/utils/captcha'
import { normalizeEmail, validateEmail } from '@/utils/authValidation'
import {
  buildSignupProfilePayload,
  normalizePhoneOrThrow,
  validateSignupProfile,
  type SignupProfileInput,
} from '@/utils/signupProfile'
import * as profileHandoff from '@/utils/signupProfileHandoff'
import { PHONE_HANDOFF_KEY } from '@/utils/signupPhone'
import { isUserExistsError } from '@/screens/signup-shared/userExists'
import type { CaptchaConfig } from '@/components/AleloCaptcha'
import { toTransactionErrorCode, type TransactionErrorLike } from '@/utils/transactionError'

type SignupIdSdk = {
  signup: (data: Record<string, string | number | boolean | undefined>) => Promise<void>
  getSignupIdentifiers?: () => Array<{ type: string; required: boolean }> | null
  getErrors?: () => TransactionErrorLike[]
  screen?: {
    loginLink?: string | null
    isCaptchaAvailable?: boolean
    captcha?: CaptchaConfig | null
    captchaProvider?: string | null
    captchaSiteKey?: string | null
    captchaImage?: string | null
  }
  transaction?: {
    requiredIdentifiers?: string[] | null
    optionalIdentifiers?: string[] | null
  }
}

async function createSdk(): Promise<SignupIdSdk | null> {
  try {
    const mod = await import('@auth0/auth0-acul-js/signup-id')
    return new mod.default() as unknown as SignupIdSdk
  } catch {
    return null
  }
}

export function useSignupIdManager() {
  const [error, setError] = useState<string>()
  const [sdk, setSdk] = useState<SignupIdSdk | null>(null)
  const [captchaToken, setCaptchaToken] = useState<string>()

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const instance = await createSdk()
      if (!cancelled) setSdk(instance)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const captchaConfig = resolveCaptcha(sdk?.screen ?? null)
  const captchaRequired = Boolean(captchaConfig?.provider)
  const onCaptchaToken = useCallback((t: string | undefined) => setCaptchaToken(t), [])

  const identifiers =
    sdk?.getSignupIdentifiers?.() ||
    [
      { type: 'username', required: true },
      { type: 'email', required: true },
      { type: 'phone', required: false },
    ]

  const needs = (type: string) =>
    identifiers.some((i) => i.type === type)

  async function handleContinue(
    opts: SignupProfileInput & {
      username: string
      email: string
      phone?: string
    },
  ) {
    setError(undefined)
    const username = digitsOnly(opts.username)
    if (needs('username') && !isValidCpf(username)) {
      throw new Error('CPF_INVALID')
    }
    const emailError = needs('email') ? validateEmail(opts.email) : null
    if (emailError) {
      throw new Error(emailError)
    }
    validateSignupProfile(opts)
    const e164 = normalizePhoneOrThrow(opts.phone)
    if (captchaRequired && !captchaToken) {
      throw new Error('CAPTCHA_REQUIRED')
    }

    const profile = buildSignupProfilePayload(opts)

    const payload: Record<string, string | undefined> = {
      ...(needs('username') || username ? { username } : {}),
      ...(needs('email') || opts.email ? { email: normalizeEmail(opts.email) } : {}),
      phone: e164,
      phoneNumber: e164,
      'ulp-phone': e164,
      ...(captchaToken ? { captcha: captchaToken } : {}),
      ...profile,
    }

    // Quem grava o perfil é a Action do `pre-user-registration`, que lê o corpo
    // do `signup()` da tela de senha — uma navegação adiante daqui. O celular
    // vai junto: a connection não o tem como identificador, então a Auth0 não
    // o devolve em `screen.data` e ele se perderia entre as telas.
    profileHandoff.save({ ...profile, [PHONE_HANDOFF_KEY]: e164 })

    const instance = sdk ?? (await createSdk())
    if (instance?.signup) {
      await instance.signup(payload)
      return
    }

    console.info('[preview signup-id]', payload)
    window.dispatchEvent(
      new CustomEvent('alelo:acul-preview', {
        detail: { screen: 'signup-id', action: 'signup', data: payload },
      }),
    )
    window.location.search = '?screen=signup-password'
  }

  function handleBackToLogin() {
    const link = sdk?.screen?.loginLink
    if (link) {
      window.location.assign(link)
      return
    }
    window.location.search = '?screen=login-id'
  }

  // Falha do cadastro volta no re-render do Auth0; vira código (a tela
  // mostra a copy), nunca o texto do provedor.
  const serverError = toTransactionErrorCode(sdk?.getErrors?.())
  // A Auth0 recusou o `signup()` porque o CPF já tem conta (store ou Get User).
  const userExists = isUserExistsError(sdk?.getErrors?.())

  return {
    handleContinue,
    handleBackToLogin,
    userExists,
    needsUsername: needs('username'),
    needsEmail: needs('email'),
    needsPhone: needs('phone'),
    captchaConfig,
    captchaReady: !captchaRequired || Boolean(captchaToken),
    onCaptchaToken,
    error: error || serverError,
  }
}
